/**
 * Utilitários para manipulação de datas sem problemas de fuso horário
 */

import { differenceInDays, format } from "date-fns";
import { ptBR } from "date-fns/locale";

/**
 * Converte uma string de data YYYY-MM-DD para um objeto Date local
 * Evita problemas de fuso horário ao trabalhar com datas
 */
export const createLocalDate = (dateString: string): Date => {
  if (!dateString) {
    throw new Error('createLocalDate: dateString is required');
  }
  // Se a string já tem formato completo (com T), usar normalmente
  if (dateString.includes('T')) {
    return new Date(dateString);
  }

  // Se é formato YYYY-MM-DD, converter para data local
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day); // month - 1 porque Date usa 0-based months
};

export const INVALID_DATE_MESSAGE = "Data inválida: confira o ano";

/**
 * Data "YYYY-MM-DD" com ano entre 2000 e 2099. O input de data aceita um ano
 * de dois dígitos ("26") como 0026, e o banco guarda: fora desse intervalo é
 * erro de digitação. A API recusa as mesmas datas.
 */
export const isPlausibleDate = (value: string): boolean => {
  if (!/^\d{4}-\d{2}-\d{2}/.test(value)) return false;
  const year = Number(value.slice(0, 4));
  return year >= 2000 && year <= 2099;
};

export const invalidDateError = (value: string | null | undefined): string | null =>
  value && !isPlausibleDate(value) ? INVALID_DATE_MESSAGE : null;

// ---------------------------------------------------------------------------
// Helpers de EXIBIÇÃO em pt-BR. Escolha pelo TIPO do dado, não pelo visual:
//
//  • formatDateBR    -> coluna DATE / string "YYYY-MM-DD" (data lógica).
//  • formatDateTime  -> timestamptz (instante real, ex.: created_at) com hora.
//  • formatDateOnly  -> timestamptz exibido só como data (sem hora).
//  • formatUtcDate   -> data lógica guardada como timestamp UTC-meia-noite
//                       (ex.: segmentos do timeline de disponibilidade).
//
// (Não confundir com `formatLocalDate(Date)` acima, que SERIALIZA um Date pra
// "YYYY-MM-DD" ao salvar.)
//
// O off-by-one de fuso (mostrar 20/04 no lugar de 21/04) acontece quando uma
// DATA LÓGICA é formatada em horário local: `new Date("2026-04-21")` vira
// UTC-meia-noite e, em GMT-3, volta um dia. Use os helpers abaixo e não
// `format(new Date(campoDate), ...)` direto.
// ---------------------------------------------------------------------------

/** Data LÓGICA (coluna DATE / "YYYY-MM-DD"). Ex.: data_inicio, data_conclusao. */
export const formatDateBR = (
  dateString: string | null | undefined,
  pattern = "dd/MM/yyyy",
): string => {
  if (!dateString) return "";
  return format(createLocalDate(dateString), pattern, { locale: ptBR });
};

/** Instante (timestamptz) em horário local, com hora. Ex.: created_at, observed_at. */
export const formatDateTime = (
  iso: string | null | undefined,
  pattern = "dd/MM/yyyy HH:mm",
): string => {
  if (!iso) return "";
  return format(new Date(iso), pattern, { locale: ptBR });
};

/** Instante (timestamptz) exibido só como data, em horário local. */
export const formatDateOnly = (
  iso: string | null | undefined,
  pattern = "dd/MM/yyyy",
): string => {
  if (!iso) return "";
  return format(new Date(iso), pattern, { locale: ptBR });
};

/**
 * Data LÓGICA guardada como timestamp UTC-meia-noite (tem "T", então
 * createLocalDate não ajuda). Formata fixando timeZone UTC pra não voltar um
 * dia. Ex.: limites de segmento do timeline de disponibilidade.
 */
export const formatUtcDate = (
  iso: string | null | undefined,
  options: Intl.DateTimeFormatOptions = { day: "2-digit", month: "2-digit", year: "2-digit" },
): string => {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("pt-BR", { timeZone: "UTC", ...options });
};

/**
 * Converte um objeto Date para string YYYY-MM-DD usando timezone local
 * Evita problemas de fuso horário ao salvar datas
 */
export const formatLocalDate = (date: Date | undefined): string | undefined => {
  if (!date) return undefined;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Instante a gravar num timestamptz a partir de um dia escolhido num campo de
 * data (ex.: vagas.data_aprovacao). Hoje vira o instante atual, igual a quando
 * não havia campo; outro dia vira meio-dia local, que cai no mesmo dia em UTC
 * e em São Paulo, então o corte por mês não muda.
 */
export const logicalDateToTimestamp = (dateString: string, now: Date = new Date()): string => {
  if (dateString === formatLocalDate(now)) return now.toISOString();
  const date = createLocalDate(dateString);
  date.setHours(12, 0, 0, 0);
  return date.toISOString();
};

/** Dia local ("YYYY-MM-DD") de um timestamptz, para preencher um input type="date". */
export const timestampToLocalDate = (iso: string | null | undefined): string => {
  if (!iso) return "";
  return formatLocalDate(new Date(iso)) ?? "";
};

/**
 * Calcula e formata o tempo decorrido de forma amigável (há X dias, há X meses, etc.)
 * Usado para exibir quando algo foi atualizado ou criado
 */
export const formatTimeAgo = (date: string | Date): { text: string; isRecent: boolean } => {
  const now = new Date();
  const targetDate = new Date(date);
  
  // Verificar se é o mesmo dia do calendário
  const isSameDay = now.getFullYear() === targetDate.getFullYear() &&
                    now.getMonth() === targetDate.getMonth() &&
                    now.getDate() === targetDate.getDate();
  
  // Normalizar as datas para meia-noite para calcular diferenças baseadas em dias calendário
  const nowDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const targetDateNormalized = new Date(targetDate.getFullYear(), targetDate.getMonth(), targetDate.getDate());
  
  // Calcular diferença em dias baseada no calendário
  const diffMs = nowDate.getTime() - targetDateNormalized.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  
  // Calcular diferença em meses baseada no calendário
  const diffMonths = (now.getFullYear() - targetDate.getFullYear()) * 12 + (now.getMonth() - targetDate.getMonth());
  
  // Calcular diferença em anos baseada no calendário
  const diffYears = now.getFullYear() - targetDate.getFullYear();

  let text: string;
  let isRecent: boolean;

  if (isSameDay) {
    text = "hoje";
    isRecent = true;
  } else if (diffDays === 1) {
    text = "há 1 dia";
    isRecent = true;
  } else if (diffDays < 30) {
    text = `há ${diffDays} dias`;
    isRecent = true;
  } else if (diffMonths === 1) {
    text = "há 1 mês";
    isRecent = diffMonths <= 3;
  } else if (diffMonths < 12) {
    text = `há ${diffMonths} meses`;
    isRecent = diffMonths <= 3;
  } else if (diffYears === 1) {
    text = "há mais de 1 ano";
    isRecent = false;
  } else {
    text = `há mais de ${diffYears} anos`;
    isRecent = false;
  }

  return { text, isRecent };
};

/**
 * Calcula quantos dias faltam até uma data de conclusão
 * Retorna número positivo se a data está no futuro, negativo se está no passado
 * @param dataConclusao Data de conclusão no formato YYYY-MM-DD
 * @returns Número de dias restantes ou null se dataConclusao for null
 */
export const calcularDiasRestantes = (dataConclusao: string | null): number | null => {
  if (!dataConclusao) return null;
  const hoje = new Date();
  const dataFim = createLocalDate(dataConclusao);
  return differenceInDays(dataFim, hoje);
}; 